import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEOHead from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { blogPosts, categories } from "@/data/blogPosts";

const blogSchema = {
  "@context": "https://schema.org",
  "@type": "Blog",
  "name": "مدونة حسن عبدالله للمقاولات والديكورات",
  "description": "مقالات ونصائح احترافية في الديكورات والدهانات والتشطيبات بالرياض",
  "url": "https://decoriyaa.com/blog",
  "publisher": {
    "@type": "Organization",
    "name": "حسن عبدالله للمقاولات والديكورات",
    "url": "https://decoriyaa.com"
  }
};

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered = activeCategory === "all"
    ? blogPosts
    : blogPosts.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <SEOHead
        title="مدونة ديكورات وتشطيبات بالرياض | حسن عبدالله"
        description="مقالات ونصائح في الديكورات والدهانات وورق الجدران والجبس بورد والباركيه. نصائح من خبراء التشطيبات في الرياض."
        canonical="/blog"
        schema={blogSchema}
      />
      <Header />

      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="bg-secondary py-16">
          <div className="container mx-auto px-4 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl font-bold text-secondary-foreground mb-4"
            >
              مدونة <span className="text-primary">الديكورات والتشطيبات</span>
            </motion.h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              نصائح ومقالات احترافية تساعدك في اختيار أفضل الديكورات والتشطيبات لمنزلك في الرياض
            </p>
          </div>
        </section>

        {/* Categories */}
        <section className="container mx-auto px-4 py-8">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </section>

        {/* Posts Grid */}
        <section className="container mx-auto px-4 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post, i) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={`/blog/${post.slug}`}>
                  <Card className="group overflow-hidden border-border hover:border-primary/40 hover:shadow-lg transition-all duration-300 h-full">
                    <div className="relative overflow-hidden aspect-video">
                      <img
                        src={post.image}
                        alt={post.imageAlt}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-3 right-3 bg-primary/90 text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.date).toLocaleDateString("ar-SA")}
                        </span>
                      </div>
                      <h2 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                        اقرأ المزيد
                        <ArrowLeft className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default BlogPage;
